import React, { useState, useEffect } from 'react';
import { ShoppingCart } from 'lucide-react';
import '../../styles/components/sections/AmazonLinksSection.css';

const EDGE_URL = process.env.REACT_APP_SUPABASE_URL
  ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-amazon-products`
  : null;

const AmazonLinksSection = ({ title }) => {
  const [products, setProducts]     = useState([]);
  const [isFallback, setIsFallback] = useState(false);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!EDGE_URL) { setLoading(false); return; }
      try {
        const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
        const resp = await fetch(EDGE_URL, {
          headers: supabaseAnonKey ? { apikey: supabaseAnonKey } : {},
        });
        if (!resp.ok) throw new Error('Network response not ok');
        const data = await resp.json();
        if (!cancelled) {
          // Handle both old array shape and new { isFallback, products } shape
          if (Array.isArray(data)) {
            setProducts(data);
            setIsFallback(false);
          } else if (data?.products) {
            setProducts(data.products);
            setIsFallback(data.isFallback === true);
          }
        }
      } catch {
        // Silently fail — section simply won't render
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading || !products || products.length === 0) return null;

  return (
    <div className="amazon-links-section">
      {title && <h3 className="amazon-links-title">{title}</h3>}

      {isFallback ? (
        /* ── Fallback mode: clean list with affiliate links ── */
        <ul className="amazon-fallback-list">
          {products.map(product => (
            <li key={product.asin} className="amazon-fallback-item">
              <a
                href={product.url}
                target="_blank"
                rel="noopener noreferrer"
                className="amazon-fallback-link"
              >
                <span className="amazon-fallback-icon"><ShoppingCart size={18} aria-hidden="true" /></span>
                <span className="amazon-fallback-label">{product.title}</span>
                <span className="amazon-badge">amazon</span>
              </a>
            </li>
          ))}
        </ul>
      ) : (
        /* ── Live mode: full product card grid ── */
        <div className="amazon-products-grid">
          {products.map(product => (
            <a
              key={product.asin}
              href={product.url}
              target="_blank"
              rel="noopener noreferrer"
              className="amazon-product-card"
            >
              {product.image && (
                <div className="amazon-product-img-wrap">
                  <img src={product.image} alt={product.title} className="amazon-product-img" />
                </div>
              )}
              <div className="amazon-product-body">
                <span className="amazon-product-title">{product.title}</span>
                {product.price && (
                  <span className="amazon-product-price">{product.price}</span>
                )}
                <span className="amazon-product-cta">
                  <span className="amazon-badge">amazon</span>
                  <span className="amazon-card-arrow">→</span>
                </span>
              </div>
            </a>
          ))}
        </div>
      )}

      <p className="amazon-disclaimer">
        As an Amazon Associate, I earn from qualifying purchases.
      </p>
    </div>
  );
};

export default AmazonLinksSection;
